import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, ArrowLeft, FileText, FileSpreadsheet, Calendar as CalendarIcon, Eye } from "lucide-react";
import api, { getToken, API, getToken as getTok } from "@/lib/api";
import { toast } from "sonner";

function weekday(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short" });
}
function dayOfMonth(dateStr) {
  return parseInt(dateStr.slice(8, 10), 10);
}

function HeatCell({ day, onClick }) {
  if (!day) return <div className="aspect-square rounded-md bg-slate-100/60" />;
  const sum = day.summary || {};
  const total = sum.total_expected || 0;
  const presentish = (sum.present || 0) + (sum.late || 0) + (sum.late_arrival || 0) + (sum.signed_out || 0);
  const pct = total > 0 ? Math.round((presentish / total) * 100) : 0;
  let bg = "bg-slate-200 text-slate-600";
  if (total === 0) bg = "bg-slate-100 text-slate-400";
  else if (sum.absent === 0 && pct >= 95) bg = "bg-emerald-500 text-white";
  else if (sum.absent <= 1 && pct >= 90) bg = "bg-emerald-300 text-emerald-900";
  else if (pct >= 80) bg = "bg-amber-300 text-amber-900";
  else if (pct >= 60) bg = "bg-orange-400 text-white";
  else bg = "bg-red-500 text-white";
  return (
    <button
      onClick={() => onClick(day)}
      className={`aspect-square rounded-md ${bg} font-semibold text-xs flex flex-col items-center justify-center hover:ring-2 hover:ring-blue-500 transition`}
      data-testid={`heat-${day.date}`}
      title={`${day.date} (${weekday(day.date)}) — ${sum.present || 0}P / ${sum.late || 0}L / ${sum.absent || 0}A of ${total}`}
    >
      <span className="text-[10px] opacity-80">{weekday(day.date)}</span>
      <span className="text-base">{dayOfMonth(day.date)}</span>
      {!day.is_locked && total > 0 && <span className="text-[8px] uppercase opacity-80">live</span>}
    </button>
  );
}

export default function Archive() {
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [days, setDays] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selected, setSelected] = useState({});
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!getToken()) navigate("/"); }, [navigate]);

  useEffect(() => {
    api.get("/seasons").then((r) => {
      const items = r.data.items || [];
      setSeasons(items);
      const active = items.find((s) => s.status === "active") || items[0];
      if (active) {
        setSeasonId(active.id || active._id);
        setStartDate(active.start_date || "");
        setEndDate(active.end_date || "");
      }
    }).catch(() => toast.error("Could not load seasons"));
  }, []);

  const loadDays = async (sid, sd, ed, status) => {
    setLoading(true);
    try {
      const params = { season_id: sid || "all", page_size: 200 };
      if (sd) params.start_date = sd;
      if (ed) params.end_date = ed;
      if (status && status !== "all") params.status = status;
      const r = await api.get("/archive/days", { params });
      setDays(r.data.items || []);
    } catch { toast.error("Failed to load archive days"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (seasonId) loadDays(seasonId, startDate, endDate, statusFilter);
  }, [seasonId, startDate, endDate, statusFilter]);

  const stats = useMemo(() => {
    const tot = days.length;
    const recs = days.reduce((a, d) => a + (d.summary?.total_expected || 0), 0);
    const presentish = days.reduce((a, d) => a + ((d.summary?.present || 0) + (d.summary?.late || 0) + (d.summary?.late_arrival || 0) + (d.summary?.signed_out || 0)), 0);
    const avg = recs > 0 ? Math.round((presentish / recs) * 100) : 0;
    const withAbs = days.filter((d) => (d.summary?.absent || 0) > 0).length;
    return { tot, recs, avg, withAbs };
  }, [days]);

  const heatGrid = useMemo(() => {
    if (!days.length) return [];
    // 7-column calendar grid
    const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
    const first = new Date(sorted[0].date + "T12:00:00");
    const last = new Date(sorted[sorted.length - 1].date + "T12:00:00");
    const startWeekday = first.getDay(); // 0=Sun
    const out = [];
    for (let i = 0; i < startWeekday; i++) out.push(null);
    const byDate = Object.fromEntries(sorted.map((d) => [d.date, d]));
    let cur = new Date(first);
    while (cur <= last) {
      const ds = cur.toISOString().slice(0, 10);
      out.push(byDate[ds] || { date: ds, summary: { total_expected: 0 }, is_locked: false });
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [days]);

  const toggleSelect = (date) => setSelected((s) => ({ ...s, [date]: !s[date] }));
  const selectedDates = Object.entries(selected).filter(([, v]) => v).map(([k]) => k).sort();

  const exportSelected = async (kind) => {
    if (selectedDates.length === 0) return toast.message("Pick at least one day");
    const sd = selectedDates[0], ed = selectedDates[selectedDates.length - 1];
    await downloadFile(`/export/range/${kind}`, { start_date: sd, end_date: ed }, `RRDC_Attendance_${sd}_to_${ed}.${kind === "csv" ? "csv" : "xlsx"}`);
  };

  const downloadFile = async (path, params, filename) => {
    try {
      const res = await api.get(path, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    } catch { toast.error("Download failed"); }
  };

  const exportSeason = (kind) => {
    if (!seasonId) return;
    const s = seasons.find((x) => (x.id || x._id) === seasonId);
    const short = s?.short_name || seasonId;
    downloadFile(`/export/season/${kind}`, { season_id: seasonId }, `RRDC_Attendance_${short}.${kind === "csv" ? "csv" : "xlsx"}`);
  };

  const exportCustom = () => {
    if (!rangeStart || !rangeEnd) return;
    downloadFile("/export/range/csv", { start_date: rangeStart, end_date: rangeEnd }, `RRDC_Attendance_${rangeStart}_to_${rangeEnd}.csv`);
    setRangeOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg text-slate-700 hover:bg-slate-100" data-testid="archive-back">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </button>
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-10 h-10 rounded-xl object-contain bg-white shadow-sm p-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rolling River — Archive</div>
              <h1 className="font-display text-lg font-bold text-slate-900">Attendance Archive</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={seasonId}
              onChange={(e) => {
                setSeasonId(e.target.value);
                const s = seasons.find((x) => (x.id || x._id) === e.target.value);
                if (s) { setStartDate(s.start_date || ""); setEndDate(s.end_date || ""); }
                setSelected({});
              }}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
              data-testid="archive-season-select"
            >
              {seasons.map((s) => (
                <option key={s.id || s._id} value={s.id || s._id}>{s.name} ({s.status})</option>
              ))}
            </select>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="archive-start" />
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="archive-end" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="archive-status-filter">
              <option value="all">All</option>
              <option value="locked">Locked Only</option>
              <option value="live">Live (Today)</option>
            </select>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="stat-days">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">Days Recorded</div>
            <div className="text-3xl font-display font-extrabold tabular-nums text-slate-900">{stats.tot}</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="stat-records">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">Attendance Records</div>
            <div className="text-3xl font-display font-extrabold tabular-nums text-slate-900">{stats.recs}</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="stat-avg">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">Avg Attendance</div>
            <div className="text-3xl font-display font-extrabold tabular-nums text-emerald-700">{stats.avg}%</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="stat-abs-days">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-bold">Days w/ Absences</div>
            <div className="text-3xl font-display font-extrabold tabular-nums text-red-700">{stats.withAbs}</div>
          </div>
        </div>

        {/* Top action row */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => exportSeason("csv")} className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="dl-season-csv">
            <FileText className="w-4 h-4" /> Full Season CSV
          </button>
          <button onClick={() => exportSeason("xlsx")} className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="dl-season-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Full Season Excel
          </button>
          <button onClick={() => setRangeOpen(true)} className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold" data-testid="dl-custom-range">
            <CalendarIcon className="w-4 h-4" /> Custom Range Export
          </button>
        </div>

        {/* Heat map */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 mb-6" data-testid="heat-map">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-display text-base font-bold">Attendance Heat Map</h2>
              <div className="text-xs text-slate-500">Click any day to view its attendance</div>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span className="inline-block w-3 h-3 rounded bg-emerald-500" /> Perfect
              <span className="inline-block w-3 h-3 rounded bg-amber-300" /> Some Late
              <span className="inline-block w-3 h-3 rounded bg-red-500" /> Many Absent
              <span className="inline-block w-3 h-3 rounded bg-slate-100" /> No data
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {["S","M","T","W","T","F","S"].map((d, i) => (
              <div key={i} className="text-center text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold">{d}</div>
            ))}
            {heatGrid.map((day, i) => (
              <HeatCell key={i} day={day} onClick={(d) => navigate(`/past-day?date=${d.date}`)} />
            ))}
          </div>
          {loading && <div className="mt-3 text-sm text-slate-500">Loading…</div>}
          {!loading && heatGrid.length === 0 && (
            <div className="mt-3 text-sm text-slate-500" data-testid="heat-empty">No archived days yet for this filter. Daily archives are created at the EOD snapshot (3:50 PM) or you can run one manually from Settings.</div>
          )}
        </div>

        {/* List view */}
        <div className="rounded-2xl bg-white border border-slate-200 overflow-x-auto">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h2 className="font-display text-base font-bold">All Days</h2>
            {selectedDates.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{selectedDates.length} selected</span>
                <button onClick={() => exportSelected("csv")} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold" data-testid="export-selected-csv">CSV</button>
                <button onClick={() => exportSelected("xlsx")} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold" data-testid="export-selected-xlsx">Excel</button>
                <button onClick={() => setSelected({})} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold">Clear</button>
              </div>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-8 px-3 py-2"></th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Date</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Day</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Status</th>
                <th className="text-right px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Present</th>
                <th className="text-right px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Late</th>
                <th className="text-right px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Absent</th>
                <th className="text-right px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Total</th>
                <th className="text-right px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">%</th>
                <th className="text-right px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {days.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-500" data-testid="list-empty">No days match this filter.</td></tr>
              )}
              {days.map((d) => {
                const sum = d.summary || {};
                const total = sum.total_expected || 0;
                const presentish = (sum.present || 0) + (sum.late || 0) + (sum.late_arrival || 0) + (sum.signed_out || 0);
                const pct = total ? Math.round((presentish / total) * 100) : 0;
                return (
                  <tr key={d.date} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2"><input type="checkbox" checked={!!selected[d.date]} onChange={() => toggleSelect(d.date)} data-testid={`select-${d.date}`} /></td>
                    <td className="px-3 py-2 font-mono tabular-nums">{d.date}</td>
                    <td className="px-3 py-2 text-slate-600">{weekday(d.date)}</td>
                    <td className="px-3 py-2">
                      {d.is_locked
                        ? <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] bg-emerald-600 text-white">Locked</span>
                        : <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] bg-amber-100 text-amber-800">Live</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{sum.present || 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{(sum.late || 0) + (sum.late_arrival || 0)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${sum.absent ? "text-red-700 font-semibold" : ""}`}>{sum.absent || 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{total}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct}%</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => navigate(`/past-day?date=${d.date}`)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="View" data-testid={`view-${d.date}`}><Eye className="w-4 h-4" /></button>
                        <button onClick={() => downloadFile("/export/csv", { date: d.date }, `rrdc-attendance-${d.date}.csv`)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="CSV"><FileText className="w-4 h-4" /></button>
                        <button onClick={() => downloadFile("/export/xlsx", { date: d.date }, `rrdc-attendance-${d.date}.xlsx`)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Excel"><FileSpreadsheet className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {rangeOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 p-6">
              <h3 className="font-display text-lg font-bold mb-3">Custom Range Export</h3>
              <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 font-bold mb-1">From</label>
              <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 mb-3" data-testid="range-start" />
              <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 font-bold mb-1">To</label>
              <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 mb-4" data-testid="range-end" />
              <div className="flex gap-2">
                <button onClick={() => setRangeOpen(false)} className="flex-1 btn-touch px-4 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold">Cancel</button>
                <button onClick={() => { exportCustom(); }} className="flex-1 btn-touch px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="range-export-csv">Export CSV</button>
                <button onClick={() => downloadFile("/export/range/xlsx", { start_date: rangeStart, end_date: rangeEnd }, `RRDC_Attendance_${rangeStart}_to_${rangeEnd}.xlsx`)} className="flex-1 btn-touch px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="range-export-xlsx">Export XLSX</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
